// src/app/modules/demandes/DemanderFormationManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import "../../RessourcesStyles/DemanderFormation.css";

import UsersPickerModal from "./UsersPickerModal";
import BulkUsersFieldsModal from "./BulkUsersFieldsModal";
import DemandeHorsCatalogueForm from "./DemandeHorsCatalogueForm";

import { fetchCataloguesRequest } from "../../../../../redux/actions/cataloguesActions";
import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { getDirectReportsRequest } from "../../../../../redux/actions/userActions";
import { createDemandeManagerBulkRequest, getCompanyDemandesRequest } from "../../../../../redux/actions/demandesActions";
import { fetchFormationsByCatalogueRequest } from "../../../../../redux/actions/formationsActions";
import {
    fetchFormationBookmarksRequest,
    toggleFormationBookmarkRequest,
} from "../../../../../redux/actions/formationBookmarksActions";

import {
    Search,
    Eye,
    Download,
    BookOpen,
    Sparkles,
    Layers,
    Calendar,
    X,
    Users,
    ChevronUp,
    Target,
    ClipboardCheck,
    Clock,
    BarChart,
    Bookmark,
} from "lucide-react";
import { API_BASE_URL } from "../../../../../../api";

const isFormationCampaign = (c) => String(c?.typeCampagne || "").toUpperCase() === "FORMATION";
const isCampaignActive = (c) => c?.status === true;
const isDemandeInactive = (status) => ["REJECTED", "CANCELLED", "WITHDRAWN"].includes(String(status || "").toUpperCase());

export default function DemanderFormationManager({
                                                     campagneId,
                                                     hideCampagneSelect = false,
                                                     selectedCampagne = null,
                                                 }) {
    const dispatch = useDispatch();
    const uid = sessionStorage.getItem("uid");

    const campagnesState = useSelector((s) => s.campagnes || {});
    const cataloguesState = useSelector((s) => s.catalogues || {});
    const formationsState = useSelector((s) => s.formations || {});
    const usersState = useSelector((s) => s.user || {});
    const demandesState = useSelector((s) => s.demandes || {});
    const { bookmarksByCampagneId = {} } = useSelector((s) => s.formationBookmarks || {});

    const campagnes = useMemo(
        () => (campagnesState.visibleList || campagnesState.list || []).filter(isFormationCampaign),
        [campagnesState.visibleList, campagnesState.list]
    );
    const cataloguesAll = cataloguesState.catalogues || cataloguesState.list || [];
    const { list: formations = [], listByCatalogue = {}, loading: formationsLoading } = formationsState;
    const { companyDemandes = [] } = demandesState;

    const teamUsers = usersState.directReports || [];

    // Campagne
    const [localCampagneId, setLocalCampagneId] = useState("");
    const campagneIdEff = hideCampagneSelect
        ? (campagneId ? Number(campagneId) : null)
        : (localCampagneId ? Number(localCampagneId) : null);

    // Tabs: "all" | "horsCatalogue" | "recommendations"
    const [catalogueId, setCatalogueId] = useState("all");
    const [search, setSearch] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    // Scroll top
    const [showScrollTop, setShowScrollTop] = useState(false);

    // Details modal
    const [detailsFormation, setDetailsFormation] = useState(null);
    const [openDetails, setOpenDetails] = useState(false);

    // Bulk flow Manager
    const [openUsersPick, setOpenUsersPick] = useState(false);
    const [openFieldsModal, setOpenFieldsModal] = useState(false);
    const [selectedFormation, setSelectedFormation] = useState(null);
    const [pickedUsers, setPickedUsers] = useState([]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
            setShowScrollTop(scrollTop > 100);
        };
        window.addEventListener("scroll", handleScroll, true);
        return () => window.removeEventListener("scroll", handleScroll, true);
    }, []);

    const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

    useEffect(() => {
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(fetchCataloguesRequest());
        dispatch(getCompanyDemandesRequest());
        if (uid) dispatch(getDirectReportsRequest(uid));
    }, [dispatch, uid]);

    useEffect(() => {
        if (campagneIdEff) {
            dispatch(fetchFormationBookmarksRequest(campagneIdEff));
        }
    }, [dispatch, campagneIdEff]);

    // auto select campagne
    useEffect(() => {
        if (!hideCampagneSelect && !localCampagneId && campagnes?.length) {
            const defaultCampagne = campagnes.find((c) => c?.isDefault) || campagnes[0];
            setLocalCampagneId(String(defaultCampagne.id));
        }
    }, [hideCampagneSelect, campagnes, localCampagneId]);

    const campagneObj = useMemo(() => {
        if (selectedCampagne) return selectedCampagne;
        if (!campagneIdEff) return null;
        return (campagnes || []).find((c) => String(c.id) === String(campagneIdEff)) || null;
    }, [campagnes, campagneIdEff, selectedCampagne]);

    const activeDemandesForCampagne = useMemo(
        () => (companyDemandes || []).filter((d) => String(d?.campagneId) === String(campagneIdEff) && !isDemandeInactive(d?.status)),
        [companyDemandes, campagneIdEff]
    );

    // reset on campagne change
    useEffect(() => {
        setCatalogueId("all");
        setSearch("");
        setCurrentPage(1);
    }, [campagneIdEff]);

    // catalogues visibles selon campagne.catalogueIds
    const visibleCatalogues = useMemo(() => {
        if (!campagneObj?.catalogueIds?.length) return [];
        const setIds = new Set(campagneObj.catalogueIds.map((x) => String(x)));
        return (cataloguesAll || []).filter((c) => setIds.has(String(c.id)));
    }, [cataloguesAll, campagneObj]);

    // fetch formations par catalogue
    useEffect(() => {
        if (!campagneIdEff) return;
        if (catalogueId === "all" || catalogueId === "recommendations") {
            visibleCatalogues.forEach((cat) => {
                if (!listByCatalogue[cat.id]) {
                    dispatch(fetchFormationsByCatalogueRequest(cat.id));
                }
            });
            return;
        }
        if (catalogueId && !isNaN(catalogueId)) {
            dispatch(fetchFormationsByCatalogueRequest(catalogueId));
        }
    }, [dispatch, campagneIdEff, catalogueId, visibleCatalogues, listByCatalogue]);

    // reset search on tab change
    useEffect(() => {
        setSearch("");
        setCurrentPage(1);
    }, [catalogueId]);

    // AgrÃ©ger toutes les formations si "all"
    const allFormations = useMemo(() => {
        if (catalogueId !== "all" && catalogueId !== "recommendations") return formations || [];
        const aggregated = [];
        const seen = new Set();
        visibleCatalogues.forEach((cat) => {
            const list = listByCatalogue[cat.id] || [];
            list.forEach((f) => {
                if (!seen.has(f.id)) { seen.add(f.id); aggregated.push(f); }
            });
        });
        return aggregated;
    }, [catalogueId, formations, listByCatalogue, visibleCatalogues]);

    const displayFormations = useMemo(() => {
        if (catalogueId === "all") return allFormations;
        if (catalogueId === "recommendations") return [];
        return listByCatalogue[catalogueId] || [];
    }, [catalogueId, allFormations, listByCatalogue]);

    const filteredFormations = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return displayFormations;
        return displayFormations.filter((f) => {
            const name = String(f?.nom || f?.name || "").toLowerCase();
            const ref = String(f?.reference || "").toLowerCase();
            const desc = String(f?.description || "").toLowerCase();
            return name.includes(q) || ref.includes(q) || desc.includes(q);
        });
    }, [displayFormations, search]);

    const totalPages = Math.ceil(filteredFormations.length / itemsPerPage) || 1;

    const paginatedFormations = useMemo(() => {
        return filteredFormations.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
    }, [filteredFormations, currentPage]);

    useEffect(() => {
        const total = Math.ceil(filteredFormations.length / itemsPerPage) || 1;
        if (currentPage > total) {
            setCurrentPage(total);
        }
    }, [filteredFormations.length, currentPage]);

    const renderPagination = ({ totalItems, currentPage, setCurrentPage, label }) => {
        const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

        if (!totalItems || totalItems <= itemsPerPage) return null;

        return (
            <div className="cicada-pagination">
                <div className="cicada-pagination-info">
                    Affichage de {(currentPage - 1) * itemsPerPage + 1}{' '}
                    Ã  {Math.min(currentPage * itemsPerPage, totalItems)}{' '}
                    sur {totalItems} {label}
                </div>

                <div className="cicada-pagination-actions">
                    <button
                        type="button"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    >
                        Précédent
                    </button>

                    <span>
                        Page {currentPage} / {totalPages}
                    </span>

                    <button
                        type="button"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    >
                        Suivant
                    </button>
                </div>
            </div>
        );
    };

    const selectedCatalogue = useMemo(() => {
        if (catalogueId === "all") return null;
        return (visibleCatalogues || []).find((c) => String(c.id) === String(catalogueId)) || null;
    }, [visibleCatalogues, catalogueId]);

    const getCataloguePdfUrl = (cat) =>
        cat?.pdfUrl || cat?.pdfLink || cat?.cataloguePdfUrl || cat?.urlPdf || cat?.lienPdf || cat?.pdf || "";

    const getUserDemandesForCampagne = (userId) =>
        activeDemandesForCampagne.filter((d) => String(d?.targetUserId) === String(userId));

    const canUserRequestFormation = (userId, formationId) => {
        const userDemandes = getUserDemandesForCampagne(userId);
        const alreadyRequested = userDemandes.some((d) => String(d?.formationId) === String(formationId));
        const quotaReached = campagneObj?.nombreMaxFormations
            ? userDemandes.length >= Number(campagneObj.nombreMaxFormations)
            : false;

        return { alreadyRequested, quotaReached, eligible: !alreadyRequested && !quotaReached };
    };

    const getEligibleUsersForFormation = (formationId) =>
        (teamUsers || []).filter((u) => canUserRequestFormation(u?.id, formationId).eligible);

    const getUnavailableUserIdsForFormation = (formationId) =>
        (teamUsers || [])
            .filter((u) => !canUserRequestFormation(u?.id, formationId).eligible)
            .map((u) => u.id);

    const getFormationActionState = (formationId) => {
        const inactiveCampaign = !isCampaignActive(campagneObj);
        const eligibleUsers = getEligibleUsersForFormation(formationId);

        if (inactiveCampaign) {
            return { disabled: true, label: "Campagne inactive", helper: "Cette campagne n'est pas active." };
        }
        if (eligibleUsers.length === 0) {
            return {
                disabled: true,
                label: "Déja demandé",
                helper: "Tous les collaborateurs éligibles ont déja cette formation ou ont atteint leur quota.",
            };
        }

        return {
            disabled: false,
            label: "Demander",
            helper: `${eligibleUsers.length} collaborateur(s) éligible(s)`,
        };
    };

    const bookmarkedFormationIds = useMemo(
        () => new Set((bookmarksByCampagneId[String(campagneIdEff)] || []).map((b) => String(b?.formationId || b?.id)).filter(Boolean)),
        [bookmarksByCampagneId, campagneIdEff]
    );

    const toggleBookmark = (event, formation) => {
        event?.stopPropagation();
        if (!campagneIdEff) return Swal.fire({ icon: "warning", text: "Sélectionne une campagne." });
        dispatch(toggleFormationBookmarkRequest({
            campagneId: campagneIdEff,
            formationId: formation?.id,
            formation,
        }));
    };

    // ---------------- Bulk flow (Manager) ----------------
    const openBulkFlow = (formation) => {
        if (!campagneIdEff) return Swal.fire({ icon: "warning", text: "Sélectionne une campagne." });
        const actionState = getFormationActionState(formation?.id);
        if (actionState.disabled) return Swal.fire({ icon: "warning", text: actionState.helper });
        setSelectedFormation(formation);
        setOpenUsersPick(true);
    };

    const onConfirmUsers = (ids) => {
        const uniqueIds = Array.from(new Set(ids || []));
        const selected = (teamUsers || []).filter(
            (u) => uniqueIds.includes(u.id) && canUserRequestFormation(u.id, selectedFormation?.id).eligible
        );
        setPickedUsers(selected);
        setOpenUsersPick(false);
        setOpenFieldsModal(true);
    };

    const onSubmitFields = (rows) => {
        if (!selectedFormation?.id) return Swal.fire({ icon: "warning", text: "Formation introuvable." });
        const payload = {
            campagneId: Number(campagneIdEff),
            type: "FORMATION",
            formationId: Number(selectedFormation.id),
            items: (rows || []).map((r) => ({
                targetUserId: Number(r.targetUserId),
                mode: r.mode || null,
                period: r.period || null,
                niveau: r.niveau || null,
                certifiante: !!r.certifiante,
                priorite: r.priorite != null ? Number(r.priorite) : null,
                objectifs: r.objectifs || null,
                objectifs2: r.objectifs2 || null,
                objectifs3: r.objectifs3 || null,
            })),
        };
        dispatch(createDemandeManagerBulkRequest(payload));
        setOpenFieldsModal(false);
        setPickedUsers([]);
        setSelectedFormation(null);
    };

    const openFormationDetails = (f) => {
        setDetailsFormation(f);
        setOpenDetails(true);
    };

    const loading = !!(campagnesState.loading || cataloguesState.loading || formationsLoading);

    return (
        <div className="dfPage">
            <div className="dfWrap">
                {/* â”€â”€ Header â”€â”€ */}
                <div className="dfHeader">
                    <div>
                        <h1 className="dfTitle">Demande de formation</h1>
                        <p className="dfSubtitle">Gérez les demandes de formation pour votre équipe</p>
                    </div>
                    <div className="dfHeaderActions" />
                </div>

                {/* â”€â”€ Campaign Bar â”€â”€ */}
                {!hideCampagneSelect && (
                    <div className="dfCampaignBar">
                        <label className="dfCampaignLabel">Campagne de formation</label>
                        <div className="dfSelectWrapper">
                            <Calendar className="dfSelectIcon" size={16} />
                            <select
                                className="dfSelect"
                                value={localCampagneId}
                                onChange={(e) => {
                                    setLocalCampagneId(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="">Sélectionnez une campagne</option>
                                {campagnes.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.nom || c.name} {c.anneeCampagne ? `(${c.anneeCampagne})` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* â”€â”€ Main card â”€â”€ */}
                <div className="dfCard">
                    {!campagneIdEff ? (
                        <div className="ae-empty">
                            <Calendar size={40} className="ae-empty-icon" />
                            <h3>Aucune campagne active</h3>
                            <p>Veuillez sélectionner une campagne de formation pour accéder au catalogue.</p>
                        </div>
                    ) : (
                        <div className="dfMainContent">
                            {/* Sub-tabs */}
                            <div className="dfSubTabs">
                                <button
                                    type="button"
                                    className={`dfSubTab ${catalogueId === "all" ? "is-active" : ""}`}
                                    onClick={() => { setCatalogueId("all"); setCurrentPage(1); }}
                                >
                                    <Layers size={14} style={{ marginRight: 6 }} />
                                    Toutes les formations
                                </button>
                                <button
                                    type="button"
                                    className={`dfSubTab ${catalogueId === "horsCatalogue" ? "is-active" : ""}`}
                                    onClick={() => { setCatalogueId("horsCatalogue"); setCurrentPage(1); }}
                                >
                                    <BookOpen size={14} style={{ marginRight: 6 }} />
                                    Demande hors catalogue
                                </button>
                                <button
                                    type="button"
                                    className={`dfSubTab ${catalogueId === "recommendations" ? "is-active" : ""}`}
                                    onClick={() => { setCatalogueId("recommendations"); setCurrentPage(1); }}
                                >
                                    <Sparkles size={14} style={{ marginRight: 6 }} />
                                    Recommandations
                                </button>

                            </div>

                            {/* Search bar */}
                            {catalogueId !== "recommendations" && catalogueId !== "horsCatalogue" && (
                                <div className="dfControls">
                                    <div className="dfSearch">
                                        <Search size={16} />
                                        <input
                                            value={search}
                                            onChange={(e) => {
                                                setSearch(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            placeholder="Rechercher une formation..."
                                        />
                                        {search && (
                                            <X size={16} className="dfSearchClear" onClick={() => { setSearch(""); setCurrentPage(1); }} />
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Content area */}
                            <div className="dfContentArea">
                                {catalogueId === "horsCatalogue" ? (
                                    <DemandeHorsCatalogueForm
                                        campagneId={campagneIdEff}
                                        currentUserRole="MANAGER"
                                        hideCampagneSelect
                                        embedded
                                        onCancel={() => { setCatalogueId("all"); setCurrentPage(1); }}
                                        onSuccess={() => { setCatalogueId("all"); setCurrentPage(1); }}
                                    />
                                ) : catalogueId === "recommendations" ? (
                                    <div className="dfRecContainer">
                                        <div className="dfRecContent">
                                            <div className="dfDevelopmentNotice">
                                                <Sparkles size={24} style={{ color: "#2f4595", marginBottom: 10 }} />
                                                <p>Aucune recommendation pour le moment</p>
                                            </div>
                                        </div>
                                    </div>
                                ) : loading && filteredFormations.length === 0 ? (
                                    <div className="dfEmpty">Chargement des formations...</div>
                                ) : (
                                    <div className="dfCataloguesList">
                                        {(catalogueId === "all" || visibleCatalogues.length === 1) ? (
                                            <>
                                                {visibleCatalogues.length === 1 && (
                                                    <div className="dfSingleCatMeta">
                                                        <span>{filteredFormations.length} formations disponibles</span>
                                                        <a
                                                            className="dfLink"
                                                            href={getCataloguePdfUrl(visibleCatalogues[0]) || undefined}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            Télécharger le catalogue
                                                        </a>
                                                    </div>
                                                )}
                                                {filteredFormations.length === 0 ? (
                                                    <div className="dfEmpty">Aucune formation disponible.</div>
                                                ) : (
                                                    <div className="dfFormationGrid">
                                                        {paginatedFormations.map((f) => (
                                                            <FormationCard
                                                                key={f.id}
                                                                f={f}
                                                                onDetails={openFormationDetails}
                                                                onDemande={openBulkFlow}
                                                                actionState={getFormationActionState(f.id)}
                                                                bookmarked={bookmarkedFormationIds.has(String(f.id))}
                                                                onToggleBookmark={toggleBookmark}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            visibleCatalogues
                                                .filter((cat) => String(cat.id) === String(catalogueId))
                                                .map((cat) => {
                                                    const catFormations = (listByCatalogue[cat.id] || []).filter((f) =>
                                                        filteredFormations.some((ff) => ff.id === f.id)
                                                    );
                                                    const paginatedCatFormations = catFormations.slice(
                                                        (currentPage - 1) * itemsPerPage,
                                                        currentPage * itemsPerPage
                                                    );
                                                    return (
                                                        <div className="dfCatalogueGroup" key={cat.id}>
                                                            <div className="dfCatalogueHeader">
                                                                <div className="dfCatalogueInfo">
                                                                    <div className="dfCatalogueTitle">{cat.nom || cat.name}</div>
                                                                    <div className="dfCatalogueCount">
                                                                        {catFormations.length} formation{catFormations.length > 1 ? "s" : ""}
                                                                    </div>
                                                                </div>
                                                                <div className="dfCatalogueActions">
                                                                    <a
                                                                        className={`dfIconBtn ${getCataloguePdfUrl(cat) ? "" : "is-disabled"}`}
                                                                        title="PDF"
                                                                        href={getCataloguePdfUrl(cat) || undefined}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        <Download size={16} />
                                                                    </a>
                                                                </div>
                                                            </div>
                                                            {catFormations.length === 0 ? (
                                                                <div className="dfNoFormations">Aucune formation correspondante.</div>
                                                            ) : (
                                                                <div className="dfFormationGrid">
                                                                    {paginatedCatFormations.map((f) => (
                                                                        <FormationCard
                                                                            key={f.id}
                                                                            f={f}
                                                                            onDetails={openFormationDetails}
                                                                            onDemande={openBulkFlow}
                                                                            actionState={getFormationActionState(f.id)}
                                                                            bookmarked={bookmarkedFormationIds.has(String(f.id))}
                                                                            onToggleBookmark={toggleBookmark}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                        )}
                                        {renderPagination({
                                            totalItems: filteredFormations.length,
                                            currentPage,
                                            setCurrentPage,
                                            label: "formations"
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* â”€â”€ Details Modal â”€â”€ */}
            {openDetails && detailsFormation && (
                <div
                    className="dfDetailsModalOverlay"
                    onMouseDown={(e) => e.target === e.currentTarget && setOpenDetails(false)}
                >
                    <div className="dfDetailsModal">
                        <div className="dfDetailsBanner">
                            {detailsFormation.picture ? (
                                <img
                                    src={`${API_BASE_URL}/api/formations/images/${detailsFormation.picture}`}
                                    alt={detailsFormation.nom}
                                />
                            ) : (
                                <div className="dfDetailsPlaceholderBanner">
                                    <span>{(detailsFormation.nom || "F").slice(0, 1).toUpperCase()}</span>
                                </div>
                            )}
                            <div className="dfDetailsBadgeTop">{detailsFormation.type || "Formation"}</div>
                            <button
                                type="button"
                                className="dfDetailsClose"
                                onClick={() => setOpenDetails(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="dfDetailsBody">
                            <h2 className="dfDetailsTitle">{detailsFormation.nom || detailsFormation.name}</h2>
                            <div className="dfDetailsTitleUnderline" />

                            <div className="dfDetailsGrid">
                                <div className="dfDetailsMain">
                                    <section className="dfDetailsSection">
                                        <div className="dfSectionHeader">
                                            <div className="dfSectionIcon"><Target size={20} /></div>
                                            <h3>Objectif(s)</h3>
                                        </div>
                                        <ul className="dfDetailsList">
                                            {(() => {
                                                const rawFields = [
                                                    detailsFormation.objectif,
                                                    detailsFormation.objectif2,
                                                    detailsFormation.objectif3,
                                                ].filter(Boolean);

                                                if (rawFields.length === 0) {
                                                    return <li className="dfNoBullet dfMuted">Aucun objectif spécifié</li>;
                                                }

                                                const allPoints = [];
                                                rawFields.forEach((val) => {
                                                    const parts = String(val)
                                                        .split(/[\n;â€¢]+/)
                                                        .map((p) => p.trim())
                                                        .filter(Boolean);
                                                    allPoints.push(...parts);
                                                });

                                                return allPoints.map((item, idx) => <li key={idx}>{item}</li>);
                                            })()}
                                        </ul>
                                    </section>

                                    <section className="dfDetailsSection">
                                        <div className="dfSectionHeader">
                                            <div className="dfSectionIcon"><ClipboardCheck size={20} /></div>
                                            <h3>Prérequis</h3>
                                        </div>
                                        <ul className="dfDetailsList">
                                            {detailsFormation.prerequis ? (
                                                detailsFormation.prerequis
                                                    .split(/[\n;]+/)
                                                    .map((p) => p.trim())
                                                    .filter(Boolean)
                                                    .map((p, i) => <li key={i}>{p}</li>)
                                            ) : (
                                                <li className="dfNoBullet dfMuted">Aucun prérequis spécifique</li>
                                            )}
                                        </ul>
                                    </section>
                                </div>

                                <aside className="dfDetailsSidebar">
                                    <div className="dfInfoCard">
                                        <h4>Informations pratiques</h4>
                                        <div className="dfInfoItem">
                                            <Users size={18} />
                                            <div>
                                                <label>Public visé</label>
                                                <span>{detailsFormation.publiqueVise || "équipe / Tous"}</span>
                                            </div>
                                        </div>
                                        <div className="dfInfoItem">
                                            <Clock size={18} />
                                            <div>
                                                <label>Durée</label>
                                                <span>
                                                    {detailsFormation.duration || detailsFormation.duree || "â€”"} {detailsFormation.typeDuree || "jour(s)"}
                                                </span>
                                            </div>
                                        </div>
                                        {/*<div className="dfInfoItem">*/}
                                        {/*    <BarChart size={18} />*/}
                                        {/*    <div>*/}
                                        {/*        <label>Niveau(x)</label>*/}
                                        {/*        <span>{detailsFormation.niveaux || "Tous niveaux"}</span>*/}
                                        {/*    </div>*/}
                                        {/*</div>*/}
                                        {/*<div className="dfInfoItem">*/}
                                        {/*    <Calendar size={18} />*/}
                                        {/*    <div>*/}
                                        {/*        <label>PÃ©riode(s)</label>*/}
                                        {/*        <span>{detailsFormation.periods || "â€”"}</span>*/}
                                        {/*    </div>*/}
                                        {/*</div>*/}
                                    </div>

                                    <button
                                        type="button"
                                        className="dfDetailsActionBtn"
                                        disabled={getFormationActionState(detailsFormation?.id).disabled}
                                        onClick={() => {
                                            setOpenDetails(false);
                                            openBulkFlow(detailsFormation);
                                        }}
                                    >
                                        <Users size={16} style={{ marginRight: 8 }} />
                                        {getFormationActionState(detailsFormation?.id).label}
                                    </button>
                                </aside>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* â”€â”€ Bulk Modals â”€â”€ */}
            <UsersPickerModal
                open={openUsersPick}
                onClose={() => setOpenUsersPick(false)}
                users={teamUsers}
                title="Sélectionner les collaborateurs de votre Ã©quipe"
                onConfirm={onConfirmUsers}
                assignedIds={getUnavailableUserIdsForFormation(selectedFormation?.id)}
            />

            <BulkUsersFieldsModal
                open={openFieldsModal}
                onClose={() => setOpenFieldsModal(false)}
                users={pickedUsers}
                title="Paramétrage par collaborateur"
                onSubmit={onSubmitFields}
            />

            {showScrollTop && (
                <button className="dfScrollTop" onClick={scrollToTop} title="Retour en haut">
                    <ChevronUp size={24} />
                </button>
            )}
        </div>
    );
}

function FormationCard({ f, onDetails, onDemande, actionState, bookmarked = false, onToggleBookmark }) {
    return (
        <div className="dfFormationCard">
            <div className="dfFormationMedia" onClick={() => onDetails(f)}>
                {f.picture ? (
                    <img
                        src={`${API_BASE_URL}/api/formations/images/${f.picture}`}
                        alt={f.nom}
                    />
                ) : (
                    <div className="dfFormationPlaceholder">
                        {(f.nom || "F").slice(0, 1).toUpperCase()}
                    </div>
                )}
                <div className="dfFormationOverlay">
                    <span className="dfOverlayBtn">Détails</span>
                </div>
            </div>

            <div className="dfFormationBody">
                <div className="dfFormationTitle" title={f.nom}>{f.nom}</div>
                <div className="dfFormationInfo">
                    <div className="dfInfoRow"><span>Domaine :</span> {f.domaineNom || f.domaine?.nom || "â€”"}</div>
                    <div className="dfInfoRow"><span>Théme :</span> {f.themeNom || f.theme?.nom || "â€”"}</div>
                    <div className="dfInfoRow"><span>Mode :</span> {f.type || " "}</div>
                    <div className="dfInfoRow"><span>Durée :</span> {f.duration || f.duree ? `${f.duration || f.duree} ${f.typeDuree || "jour(s)"}` : "â€”"}</div>
                </div>
                <div className="dfFormationFooter">
                    <button
                        type="button"
                        className="dfSubmitBtn dfrf-submitBtn"
                        disabled={actionState?.disabled}
                        onClick={() => onDemande(f)}
                    >
                        <Users size={14} style={{ marginRight: 6 }} />
                        {actionState?.label || "Demander"}
                    </button>
                    <div className="dfCardActions">
                        <button
                            type="button"
                            className={`dfActionBtn ${bookmarked ? "is-bookmarked" : ""}`}
                            title="Sauvegarder"
                            onClick={(event) => onToggleBookmark?.(event, f)}
                        >
                            <Bookmark size={18} fill={bookmarked ? "currentColor" : "none"} />
                        </button>
                        <button
                            type="button"
                            className="dfActionBtn"
                            onClick={() => onDetails(f)}
                            title="Détails"
                        >
                            <Eye size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

